import { Client } from '@stomp/stompjs';

const HEARTBEAT_MS = 25_000;
const RECONNECT_DELAY_MS = 5_000;

// Exact messages the backend WS CONNECT handler (StompAuthChannelInterceptor) puts on
// a rejected-connect ERROR frame. These are terminal: retrying with the same token only
// loops (e.g. a token revoked from another device stays revoked), so we tear down and
// raise an auth error instead of reconnecting every RECONNECT_DELAY_MS.
const AUTH_REJECTION_MESSAGES = new Set([
  'Missing Authorization header on CONNECT',
  'Authorization must be Bearer',
  'Invalid token',
  'Token revoked',
  'Unknown user',
  'Account disabled',
]);

class RealtimeService {
  constructor() {
    this.client = null;
    this.tokenProvider = null;
    this.subs = new Map();  // destination → { id, callbacks: Set<fn> }
    this.connectListeners = new Set();
    this.errorListeners = new Set();
    this.authErrorListeners = new Set();
  }

  /**
   * Start the live-sync connection.
   *
   * The access token is NOT captured up front. Instead `tokenProvider` is invoked
   * immediately before every (re)connect, so the socket always presents a
   * currently-valid JWT — even after the original login token has expired and been
   * silently rotated by the HTTP layer. This is what keeps live sync alive past the
   * 30-minute access-token TTL instead of looping connect → "Invalid token" forever.
   *
   * @param {{ tokenProvider: () => Promise<string> }} opts
   *   tokenProvider resolves a valid access token (refreshing if needed) or rejects
   *   when no valid session can be obtained.
   */
  start({ tokenProvider }) {
    this.tokenProvider = tokenProvider;
    // Already running — the provider supplies fresh creds on each connect, so there
    // is nothing to restart. (Re-invoking start() on re-render is a no-op.)
    if (this.client) return;

    const wsUrl = this._resolveWsUrl();

    this.client = new Client({
      brokerURL: wsUrl,
      reconnectDelay: RECONNECT_DELAY_MS,
      heartbeatIncoming: HEARTBEAT_MS,
      heartbeatOutgoing: HEARTBEAT_MS,
      debug: () => {},
      // Runs before each CONNECT (initial + every auto-reconnect). Refreshes the
      // credential just-in-time so a reconnect never carries a stale/expired token.
      beforeConnect: async () => {
        try {
          const token = await this.tokenProvider();
          // stop() may have run (e.g. logout) while the token fetch was in flight,
          // nulling this.client — bail rather than throw on a dead reference.
          if (!this.client) return;
          this.client.connectHeaders = { Authorization: `Bearer ${token}` };
        } catch (err) {
          if (!this.client) return;
          // No valid token and refresh failed (e.g. the 7-day refresh token expired).
          // Stop the reconnect loop instead of hammering the server with doomed
          // CONNECTs, and surface an auth error so the app can route to sign-in.
          this.client.connectHeaders = {};
          // Defer teardown so we don't deactivate the client from inside its own
          // beforeConnect hook.
          setTimeout(() => this.stop(), 0);
          this.authErrorListeners.forEach((fn) => fn(err));
        }
      },
      onConnect: () => {
        for (const [destination, entry] of this.subs.entries()) {
          entry.id = this.client.subscribe(destination, (frame) => {
            const data = JSON.parse(frame.body);
            entry.callbacks.forEach((cb) => cb(data));
          });
        }
        this.connectListeners.forEach((fn) => fn());
      },
      onStompError: (frame) => {
        const message = frame.headers['message'] || 'STOMP error';
        if (AUTH_REJECTION_MESSAGES.has(message)) {
          // CONNECT was rejected for an auth reason (revoked/invalid token, disabled
          // account). Reconnecting can't fix it — stop the loop and signal auth failure
          // so the app routes to sign-in. Defer teardown to stay out of the callback.
          setTimeout(() => this.stop(), 0);
          this.authErrorListeners.forEach((fn) => fn(new Error(message)));
          return;
        }
        this.errorListeners.forEach((fn) => fn(new Error(message)));
      },
      onWebSocketError: (err) => {
        this.errorListeners.forEach((fn) => fn(err));
      },
    });

    this.client.activate();
  }

  stop() {
    if (this.client) {
      this.client.deactivate();
      this.client = null;
    }
    this.subs.clear();
    this.tokenProvider = null;
  }

  subscribe(destination, callback) {
    let entry = this.subs.get(destination);
    if (!entry) {
      entry = { id: null, callbacks: new Set() };
      this.subs.set(destination, entry);
      if (this.client && this.client.connected) {
        entry.id = this.client.subscribe(destination, (frame) => {
          const data = JSON.parse(frame.body);
          entry.callbacks.forEach((cb) => cb(data));
        });
      }
    }
    entry.callbacks.add(callback);

    return () => {
      entry.callbacks.delete(callback);
      if (entry.callbacks.size === 0) {
        if (entry.id && this.client && this.client.connected) {
          entry.id.unsubscribe();
        }
        this.subs.delete(destination);
      }
    };
  }

  onConnect(fn) {
    this.connectListeners.add(fn);
    return () => this.connectListeners.delete(fn);
  }

  onError(fn) {
    this.errorListeners.add(fn);
    return () => this.errorListeners.delete(fn);
  }

  /** Notified when the connection can't authenticate and no refresh is possible. */
  onAuthError(fn) {
    this.authErrorListeners.add(fn);
    return () => this.authErrorListeners.delete(fn);
  }

  _resolveWsUrl() {
    const base = import.meta.env.VITE_API_BASE_URL || '/api';
    if (base.startsWith('http')) {
      return base.replace(/^http/, 'ws').replace(/\/api\/?$/, '/ws');
    }
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/ws`;
  }
}

export const realtime = new RealtimeService();
